import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { getApiAsync } from "../../../../api";
import { BotStep, SortOrder } from "../../../../api/graphql";

type BotStepPanelProps = { parentId: string };

export const BotStepPanel: React.FC<BotStepPanelProps> = ({ parentId }) => {
  const rootStore = useRootStore();
  // Extraídos del rootStore para que ESLint vea la cadena de deps del useEffect.
  const { langStore, authStore, activityStore } = rootStore;
  const [items, setItems] = useState<BotStep[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        if (cancelled) return;
        const api = await getApiAsync(authStore, activityStore);
        const result = await api.botStepListWithOffset({
          where: { parent_BotPhase_steps: { id: { equals: parentId } } },
          orderBy: [{ stepNumber: SortOrder.Asc }],
          take: 100,
          offset: 0,
        });
        if (!cancelled) {
          setItems(result.botStepListWithOffset as BotStep[]);
        }
      } catch (error) {
        console.error("Failed to load botStep:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [parentId, authStore, activityStore]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
      >
        {langStore.BotStepLabels?.p || "BotStep"}
        {loading && <CircularProgress size={16} />}
        {!loading && items.length === 0 && (
          <Typography component="span" variant="body2" color="text.secondary">
            ({langStore.noRecordsMessage})
          </Typography>
        )}
      </Typography>
      {!loading && items.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {langStore.BotStepStepNumberLabel || "stepNumber"}
              </TableCell>
              <TableCell>{langStore.BotStepStepIdLabel || "stepId"}</TableCell>
              <TableCell>{langStore.BotStepStatusLabel || "status"}</TableCell>
              <TableCell>
                {langStore.BotStepDurationLabel || "duration"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <React.Fragment key={item.id}>
                <TableRow
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    rootStore.openDrawerView("botStep", item.id);
                  }}
                >
                  <TableCell>{String(item.stepNumber ?? "-")}</TableCell>
                  <TableCell>{String(item.stepId ?? "-")}</TableCell>
                  <TableCell>{String(item.status ?? "-")}</TableCell>
                  <TableCell>{String(item.duration ?? "-")}</TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};
